import type {
  AdminApiVoiceUsage,
  VoiceCreditUsageByAgent,
} from "@repo/app-types/entities/creditUsage";
import {
  MetronomeErrorCode,
  resolvePeriodCreditCapacity,
} from "@repo/app-types/entities/metronome";
import { TableSkeleton } from "@repo/ui/Skeletons/skeletons";
import { Fragment, type FC } from "react";
import { useVoiceUsageQuery } from "../../../api/entities/company.js";
import { SettingsTable } from "../shared/settingsTable.js";
import { SettingsTableBodySection } from "../shared/settingsTableBodySection.js";
import { SettingsTableDataCell } from "../shared/settingsTableDataCell.js";
import { SettingsTableHeaderCell } from "../shared/settingsTableHeaderCell.js";
import { SettingsTableHeaderSection } from "../shared/settingsTableHeaderSection.js";
import {
  CreditUsageStatus,
  UsageBar,
  useCreditUsageSection,
} from "./creditUsageSection.js";
import {
  formatCoverage,
  formatPeriodLabel,
  formatUsageAgainstIncluded,
  formatWholeNumber,
} from "./usageFormatting.js";

interface VoiceCreditsSectionProps {
  periodOffset: number;
}

/**
 * The voice agent section of the billing page: prepaid minutes remaining and
 * this period's usage per agent, for the period the page-level header
 * selects. One voice credit is one billable minute, so the section speaks in
 * minutes throughout.
 */
export const VoiceCreditsSection: FC<VoiceCreditsSectionProps> = (props) => {
  const { periodOffset } = props;
  const state = useCreditUsageSection(useVoiceUsageQuery, periodOffset);
  const { selectedUsage, usageErrorCode, isUsageError, isUsageLoading } = state;

  // Nothing to show a company that has never been on a Metronome contract.
  if (usageErrorCode === MetronomeErrorCode.NOT_FOUND && periodOffset === 0) {
    return null;
  }

  return (
    <section>
      <h2 className="text-base font-semibold text-foreground">
        Voice agent minutes
      </h2>

      <div className="mt-4">
        <CreditUsageStatus
          loadErrorTitle="Unable to load voice agent usage"
          noDataMessage="No voice agent usage for this period"
          state={state}
        >
          {selectedUsage ? <UsageOverview usage={selectedUsage} /> : null}
        </CreditUsageStatus>
      </div>
      {isUsageError ? null : (
        <div className="mt-4">
          <UsageByAgent
            agents={selectedUsage?.agents ?? []}
            isLoading={isUsageLoading}
          />
        </div>
      )}
    </section>
  );
};

interface UsageOverviewProps {
  usage: AdminApiVoiceUsage;
}

const UsageOverview: FC<UsageOverviewProps> = ({ usage }) => {
  const { balances } = usage;
  const isCurrentPeriod = usage.selected_period.offset === 0;
  // Minutes are prepaid pools, so a live period's capacity is simply what it
  // can still spend on top of what it has; a closed period has no bar.
  const capacity = resolvePeriodCreditCapacity(usage);
  const barCapacity = capacity !== null && capacity > 0 ? capacity : null;

  const details = [
    formatUsageAgainstIncluded(usage.credits_used, barCapacity, "minutes used"),
    ...formatCoverage(usage.coverage, "minutes"),
    balances.kind === "at_period_close" &&
    balances.remaining_purchased_credits > 0
      ? `${formatWholeNumber(balances.remaining_purchased_credits)} purchased minutes left at period close`
      : null,
    usage.credits_overage > 0
      ? `${formatWholeNumber(usage.credits_overage)} beyond your balance, billed per minute`
      : null,
    formatPeriodLabel(usage.selected_period),
  ].filter((detail) => detail !== null);

  return (
    <section className="rounded-md border border-border-light bg-white px-5 py-4">
      <div className="flex items-center justify-between gap-4">
        <p className="text-sm font-semibold text-foreground">
          {balances.kind === "live" && isCurrentPeriod
            ? "Prepaid minutes"
            : "Minutes used"}
        </p>
        <p className="shrink-0 text-sm font-semibold text-foreground tabular-nums">
          {balances.kind === "live" && isCurrentPeriod
            ? `${formatWholeNumber(balances.remaining_credits)} left`
            : formatWholeNumber(usage.credits_used)}
        </p>
      </div>
      {barCapacity !== null ? (
        <UsageBar
          className="mt-3 w-full"
          isUncovered={usage.credits_overage > 0}
          percentage={(usage.credits_used / barCapacity) * 100}
        />
      ) : null}
      <div className="mt-2 text-xs text-muted-foreground">
        <p>
          {details.map((detail, index) => (
            <Fragment key={detail}>
              {index > 0 ? <span aria-hidden="true"> · </span> : null}
              {detail}
            </Fragment>
          ))}
        </p>
      </div>
    </section>
  );
};

interface UsageByAgentProps {
  agents: readonly VoiceCreditUsageByAgent[];
  isLoading: boolean;
}

const UsageByAgent: FC<UsageByAgentProps> = ({ agents, isLoading }) => (
  <section className="space-y-4">
    <h2 className="text-base font-semibold text-foreground">Usage by agent</h2>
    <SettingsTable>
      <SettingsTableHeaderSection>
        <tr>
          <SettingsTableHeaderCell className="w-[70%]">
            Voice agent
          </SettingsTableHeaderCell>
          <SettingsTableHeaderCell className="w-[30%] text-right">
            Minutes used
          </SettingsTableHeaderCell>
        </tr>
      </SettingsTableHeaderSection>
      <SettingsTableBodySection>
        {isLoading ? <TableSkeleton /> : null}
        {agents.map((agent) => (
          <tr className="hover:bg-gray-50" key={agent.voice_agent_id}>
            <SettingsTableDataCell>
              {agent.voice_agent_name || "Unnamed agent"}
            </SettingsTableDataCell>
            <SettingsTableDataCell className="text-right tabular-nums">
              {formatWholeNumber(agent.credits_used)}
            </SettingsTableDataCell>
          </tr>
        ))}
      </SettingsTableBodySection>
    </SettingsTable>
    {!isLoading && agents.length === 0 ? (
      <p className="text-sm text-muted-foreground">
        No voice agent calls this period
      </p>
    ) : null}
  </section>
);
